import React from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";
import GuidanceDialogShell from "./GuidanceDialogShell";
import {
  guidanceOutlinedButtonSx,
  guidancePrimaryButtonSx
} from "./guidanceDialogStyles";

export interface ConfirmActionDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmColor?: "error" | "primary";
  loading?: boolean;
  icon?: React.ReactNode;
  dataTestId?: string;
}

const ConfirmActionDialog: React.FC<ConfirmActionDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel,
  cancelLabel,
  confirmColor = "error",
  loading = false,
  icon,
  dataTestId
}) => {
  const { t } = useKivunovaTranslation();

  return (
    <GuidanceDialogShell
      open={open}
      onClose={loading ? () => undefined : onClose}
      title={title}
      icon={
        icon ?? (
          <WarningAmberRoundedIcon
            sx={{ fontSize: 20, color: "warning.main" }}
          />
        )
      }
      maxWidth="xs"
      dataTestId={dataTestId}
      footer={
        <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
          <Button
            size="medium"
            onClick={onClose}
            variant="outlined"
            disabled={loading}
            sx={{ ...guidanceOutlinedButtonSx, flex: 1 }}
          >
            {cancelLabel ?? t("no", "No")}
          </Button>
          <LoadingButton
            size="medium"
            onClick={onConfirm}
            variant="contained"
            color={confirmColor}
            loading={loading}
            sx={{
              ...guidancePrimaryButtonSx,
              flex: 1,
              ...(confirmColor === "error"
                ? {
                    backgroundColor: "error.main",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "error.dark",
                      boxShadow: "none"
                    }
                  }
                : {})
            }}
          >
            {confirmLabel ?? t("yes", "Yes")}
          </LoadingButton>
        </Stack>
      }
    >
      {description ? (
        <Box sx={{ textAlign: "center" }}>
          {typeof description === "string" ? (
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          ) : (
            description
          )}
        </Box>
      ) : null}
    </GuidanceDialogShell>
  );
};

export default ConfirmActionDialog;
